/*
 * Spend tiers — shared helpers.
 * Tier data comes from <script id="SpendTiersData"> (snippets/spend-tiers.liquid),
 * which is built from Theme settings → Spend tiers. All money is in cents.
 */
(function () {
  if (window.SpendTiers) return;

  let data = { enabled: false, freeShipping: 0, saleEnd: '', tiers: [] };
  try {
    const el = document.getElementById('SpendTiersData');
    if (el) data = JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[SpendTiers] could not read tier settings', e);
  }

  const tiers = (data.tiers || []).filter((t) => t.min > 0).sort((a, b) => a.min - b.min);

  function highestTier(total) {
    let found = null;
    tiers.forEach((t) => {
      if (total >= t.min) found = t;
    });
    return found;
  }

  function nextTier(total) {
    return tiers.find((t) => total < t.min) || null;
  }

  function formatMoney(cents) {
    const dollars = cents / 100;
    return '$' + dollars.toLocaleString('en-US', {
      minimumFractionDigits: cents % 100 ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json());
  }

  // The cart total tiers are measured against. Shopify checks tier minimums against
  // prices before other discounts (e.g. BUY2GET10), i.e. the original total.
  function cartBasis(cart) {
    return cart ? cart.original_total_price : 0;
  }

  // One shared /cart.js request per cart change, reused by every product block on the page.
  let cartPromise = null;
  function getCart(force) {
    if (force || !cartPromise) cartPromise = fetchCart().catch(() => null);
    return cartPromise;
  }

  function qtyInCart(cart, variantId) {
    if (!cart) return 0;
    return cart.items
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((n, item) => n + item.quantity, 0);
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  const SpendTiers = {
    enabled: !!data.enabled && tiers.length > 0,
    freeShipping: data.freeShipping || 0,
    saleEnd: data.saleEnd || '',
    tiers,
    highestTier,
    nextTier,
    formatMoney,
    fetchCart,
    getCart,
    cartBasis,
    qtyInCart,
  };
  window.SpendTiers = SpendTiers;

  /* ------------------------------------------------------------------
   * Product page: live "Discount level" preview + stock cap.
   * One controller per product block ([data-js-product-component]:
   * main product, featured product, quick view).
   * Projected total = cart subtotal + selected variant price × quantity.
   * ------------------------------------------------------------------ */
  const ICON_ACTIVE = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  const ICON_LOCKED = '<svg width="10" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>';
  const QTY_SELECTOR = '[data-js-product-quantity] input[name="quantity"]';

  class ProductTiers {
    constructor(root) {
      this.root = root;
      this.cart = null;
      // After a successful add the selection is already in the cart: don't count it twice
      // until the shopper touches quantity or variant again.
      this.selectionInCart = false;

      const table = root.querySelector('[data-spend-tiers-table]');
      this.basisFallback = table ? parseInt(table.dataset.cartBasis, 10) || 0 : 0;
      this.totalFallback = table ? parseInt(table.dataset.cartTotal, 10) || 0 : 0;

      const debouncedUpdate = debounce(() => this.update({ clamp: true }), 250);
      root.addEventListener('input', (e) => {
        if (e.target.matches(QTY_SELECTOR)) {
          this.selectionInCart = false;
          this.update();
          debouncedUpdate();
        }
      });
      root.addEventListener('change', (e) => {
        if (e.target.matches(QTY_SELECTOR)) {
          this.selectionInCart = false;
          this.update({ clamp: true });
        }
      });
      // VARIANT_CHANGE and add-to-cart don't bubble: listen in the capture phase.
      root.addEventListener('VARIANT_CHANGE', () => {
        this.selectionInCart = false;
        this.update({ clamp: true });
      }, true);
      root.addEventListener('add-to-cart', () => {
        this.selectionInCart = true;
        this.refresh(true);
      }, true);

      this.update();
      this.refresh(false);
    }

    refresh(force) {
      return getCart(force).then((cart) => {
        if (cart) this.cart = cart;
        this.update({ clamp: true });
      });
    }

    get qtyInput() {
      return this.root.querySelector(QTY_SELECTOR);
    }

    get variantId() {
      const input = this.root.querySelector('product-form input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]');
      return input ? input.value : null;
    }

    variantInfo() {
      const id = this.variantId;
      const el = this.root.querySelector('[data-qty-stock]');
      let list = [];
      try {
        if (el) list = JSON.parse(el.textContent);
      } catch (e) {}
      let info = list.find((v) => String(v.id) === String(id));
      if (!info) {
        // No quantity selector on this block: fall back to the current variant JSON.
        try {
          const v = JSON.parse(this.root.querySelector('[data-js-variant-data]').textContent);
          info = { id: v.id, price: v.price, stock: 0, capped: false };
        } catch (e) {
          info = null;
        }
      }
      return info;
    }

    // How many more of this variant can be added; Infinity when stock isn't capped.
    capFor(info) {
      if (!info || !info.capped) return Infinity;
      return Math.max(0, info.stock - qtyInCart(this.cart, info.id));
    }

    selectedQty() {
      const input = this.qtyInput;
      if (!input) return 1;
      const n = parseInt(input.value, 10);
      return isNaN(n) || n < 0 ? 0 : n;
    }

    update(opts = {}) {
      const info = this.variantInfo();
      if (!info) return;
      const cap = this.capFor(info);
      if (this.applyCap) this.applyCap(info, cap, opts);
      this.renderTable(info, cap);
    }

    renderTable(info, cap) {
      const table = this.root.querySelector('[data-spend-tiers-table]');
      if (!table || !SpendTiers.enabled) return;

      const basis = this.cart ? cartBasis(this.cart) : this.basisFallback;
      // Nothing selected counts once it's in the cart, or when no more can be added.
      const qty = this.selectionInCart || cap === 0 ? 0 : this.selectedQty();
      const projected = basis + info.price * qty;
      // Free shipping is checked by Shopify against the total after discounts.
      const shipProjected = (this.cart ? this.cart.total_price : this.totalFallback) + info.price * qty;

      let current = null;
      let currentMin = -1;
      const rows = table.querySelectorAll('[data-tier-row]');
      rows.forEach((row) => {
        const min = parseInt(row.dataset.tierMin, 10);
        const on = (row.hasAttribute('data-tier-shipping') ? shipProjected : projected) >= min;
        row.classList.toggle('active', on);
        row.classList.toggle('locked', !on);
        row.classList.remove('current');
        const icon = row.querySelector('[data-tier-icon]');
        if (icon && icon.classList.contains('active-icon') !== on) {
          icon.classList.toggle('active-icon', on);
          icon.classList.toggle('locked-icon', !on);
          icon.innerHTML = on ? ICON_ACTIVE : ICON_LOCKED;
        }
        if (on && min > currentMin) {
          current = row;
          currentMin = min;
        }
      });
      rows.forEach((row) => {
        const badge = row.querySelector('[data-tier-code]');
        if (badge) badge.hidden = row !== current;
      });
      if (current) current.classList.add('current');

      const nudge = table.querySelector('[data-tier-nudge]');
      if (nudge) nudge.textContent = this.nudgeText(projected, info, cap, qty);
    }

    nudgeText(projected, info, cap, qty) {
      const cur = highestTier(projected);
      const next = nextTier(projected);
      if (!next) {
        return cur ? `You've unlocked our biggest discount — ${cur.label}.` : '';
      }
      const label = qty === 0 ? 'In your cart' : 'With this selection';
      let text = `${label}: ${formatMoney(projected)}`;
      text += cur ? ` — you unlock ${cur.label}.` : '.';
      const need = next.min - projected;
      const units = info.price > 0 ? Math.ceil(need / info.price) : Infinity;
      if (units <= cap - qty) {
        text += ` Add ${units} more to unlock ${next.label}.`;
      } else {
        text += ` Add ${formatMoney(need)} more to unlock ${next.label}.`;
      }
      return text;
    }
  }

  function initProductBlocks(scope) {
    (scope || document).querySelectorAll('[data-js-product-component]').forEach((root) => {
      if (root._spendTiers) return;
      if (!root.querySelector('[data-qty-stock], [data-spend-tiers-table]')) return;
      root._spendTiers = new ProductTiers(root);
    });
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  onReady(() => {
    initProductBlocks();
    // Quick view content is injected after load.
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.matches('[data-js-product-component]') || node.querySelector('[data-js-product-component]'))) {
            initProductBlocks(node.parentNode || document);
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  });

  /* ------------------------------------------------------------------
   * Stock cap (variants with inventory tracked by Shopify + "deny" policy).
   * Max = stock − quantity of this variant already in the cart.
   * ------------------------------------------------------------------ */
  function msgMax(n) {
    return `You've reached the maximum — only ${n} left in stock.`;
  }
  const MSG_ALL_IN_CART = 'All available stock is already in your cart.';

  // One aria-live notice per spot, created empty so screen readers announce changes.
  function noticeAfter(anchor) {
    let notice = anchor.nextElementSibling;
    if (!notice || !notice.classList.contains('qty-cap-notice')) {
      notice = document.createElement('p');
      notice.className = 'alert alert--error text-size--small qty-cap-notice';
      notice.setAttribute('aria-live', 'polite');
      notice.hidden = true;
      anchor.insertAdjacentElement('afterend', notice);
    }
    return notice;
  }

  function setNotice(notice, text) {
    if (!notice) return;
    if (text) {
      notice.textContent = text;
      notice.hidden = false;
    } else {
      notice.textContent = '';
      notice.hidden = true;
    }
  }

  ProductTiers.prototype.noticeAnchor = function () {
    const qty = this.root.querySelector('[data-js-product-quantity]');
    if (qty) return qty.closest('.cs_buy_btn_wrap') || qty;
    const form = this.root.querySelector('product-form form');
    return form || null;
  };

  ProductTiers.prototype.applyCap = function (info, cap, opts) {
    const input = this.qtyInput;
    const anchor = this.noticeAnchor();
    const notice = anchor ? noticeAfter(anchor) : null;
    const addBtn = this.root.querySelector('product-form [name="add"], product-form [data-js-product-add-to-cart]');
    const plus = input ? input.closest('[data-js-product-quantity]').querySelector('.qty-plus') : null;
    const inCart = qtyInCart(this.cart, info.id);

    // Undo anything a previous variant's cap set.
    if (addBtn && addBtn.dataset.stockCapped) {
      addBtn.disabled = false;
      addBtn.classList.remove('disabled');
      delete addBtn.dataset.stockCapped;
    }

    if (!isFinite(cap)) {
      if (input && input.dataset.stockCap) {
        input.removeAttribute('max');
        delete input.dataset.stockCap;
        if (plus) plus.classList.remove('disabled');
      }
      setNotice(notice, '');
      return;
    }

    if (cap === 0) {
      if (input) {
        input.setAttribute('max', 1);
        input.dataset.stockCap = '0';
        input.value = 1;
        if (plus) plus.classList.add('disabled');
      }
      if (inCart > 0) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.classList.add('disabled');
          addBtn.dataset.stockCapped = '1';
        }
        setNotice(notice, MSG_ALL_IN_CART);
      } else {
        // Sold out and nothing in the cart: the theme's own sold-out state handles it.
        setNotice(notice, '');
      }
      return;
    }

    if (!input) {
      setNotice(notice, '');
      return;
    }
    input.setAttribute('max', cap);
    input.dataset.stockCap = String(cap);
    let qty = parseInt(input.value, 10);
    if (opts && opts.clamp && qty > cap) {
      input.value = cap;
      qty = cap;
    }
    if (plus) plus.classList.toggle('disabled', qty >= cap);
    setNotice(notice, qty >= cap ? msgMax(cap) : '');
  };

  // Called from component-product-form.js when /cart/add.js returns 422.
  // Re-read live stock for this product block (the page's numbers may be stale).
  function refreshStock(root, productForm) {
    const section = root.closest('.shopify-section');
    const url = productForm.dataset.productUrl;
    const stockEl = root.querySelector('[data-qty-stock]');
    if (!section || !url || !stockEl) return Promise.resolve();
    const sectionId = section.id.replace('shopify-section-', '');
    return fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sectionId)}`)
      .then((r) => r.text())
      .then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-qty-stock]');
        if (fresh) stockEl.textContent = fresh.textContent;
      })
      .catch(() => {});
  }

  SpendTiers.showStockError = function (productForm, message) {
    const found = String(message || '').match(/only add (\d+)|all (\d+) /i);
    const parsed = found ? parseInt(found[1] || found[2], 10) : null;
    const root = productForm.closest('[data-js-product-component]');
    const ctrl = root && root._spendTiers;
    if (ctrl) {
      Promise.all([getCart(true), refreshStock(root, productForm)]).then(([cart]) => {
        if (cart) ctrl.cart = cart;
        const info = ctrl.variantInfo();
        const cap = ctrl.capFor(info);
        ctrl.update({ clamp: true });
        const anchor = ctrl.noticeAnchor();
        if (!anchor) return;
        const available = isFinite(cap) ? cap : parsed;
        if (available === 0) setNotice(noticeAfter(anchor), MSG_ALL_IN_CART);
        else if (available !== null) setNotice(noticeAfter(anchor), msgMax(available));
        else setNotice(noticeAfter(anchor), message);
      });
      return true;
    }
    // Product cards / quick buy: no quantity selector, just say what Shopify said, with the number.
    const form = productForm.querySelector('form');
    if (!form) return false;
    setNotice(noticeAfter(form), parsed !== null ? (parsed === 0 ? MSG_ALL_IN_CART : msgMax(parsed)) : message);
    return true;
  };

  // Cart notices go after the row's quantity box, never inside it: on the cart page that box
  // sizes to its content and "Remove" is positioned from its width (custom.css), so a notice
  // inside would push "Remove" off the card.
  function cartNoticeAnchor(input) {
    return input.closest('.cart__quantity') || input.closest('.cart-item__actions') || input.closest('product-quantity');
  }

  // Cart page + drawer steppers: typed values above max snap back, with the same notice.
  document.addEventListener('input', (e) => {
    const input = e.target;
    if (!input.matches('[data-js-cart-item] input.qty[max]')) return;
    const max = parseInt(input.getAttribute('max'), 10);
    const holder = cartNoticeAnchor(input);
    if (isNaN(max) || !holder) return;
    if (parseInt(input.value, 10) >= max) {
      if (parseInt(input.value, 10) > max) input.value = max;
      setNotice(noticeAfter(holder), msgMax(max));
    } else {
      setNotice(noticeAfter(holder), '');
    }
  }, true);

  document.addEventListener('click', (e) => {
    const plus = e.target.closest('[data-js-cart-item] .qty-plus');
    if (!plus) return;
    const stepper = plus.closest('product-quantity');
    const input = stepper && stepper.querySelector('input.qty[max]');
    if (!input) return;
    const max = parseInt(input.getAttribute('max'), 10);
    if (parseInt(input.value, 10) >= max) setNotice(noticeAfter(cartNoticeAnchor(input)), msgMax(max));
  }, true);

  // Any cart change elsewhere (cart page steppers, drawer) → refresh product blocks.
  document.addEventListener('cart-updated', () => {
    getCart(true).then(() => {
      document.querySelectorAll('[data-js-product-component]').forEach((root) => {
        if (root._spendTiers) root._spendTiers.refresh(false);
      });
    });
  }, true);

  SpendTiers.ProductTiers = ProductTiers;

  /* ------------------------------------------------------------------
   * Checkout: apply the highest qualifying tier code at click time.
   * Hooks the form submit (not the click), so the ship-date check in
   * snippets/date-picker-cart.liquid still runs first and can cancel it.
   * The code is added through /cart/update.js — keeping any non-tier codes
   * (e.g. BUY2GET10) and dropping stale lower-tier codes — then the form is
   * submitted as normal so the note, ship date and quantities are saved.
   * ------------------------------------------------------------------ */
  const tierCodes = tiers.map((t) => (t.code || '').toUpperCase()).filter(Boolean);

  function codesFor(cart) {
    const current = (cart.discount_codes || []).map((d) => (d.code || '').toUpperCase()).filter(Boolean);
    const tier = highestTier(cartBasis(cart));
    const keep = current.filter((c) => !tierCodes.includes(c));
    if (tier && tier.code) keep.push(tier.code.toUpperCase());
    return { codes: keep, tier, changed: keep.join(',') !== current.join(',') };
  }

  document.addEventListener('submit', (e) => {
    const submitter = e.submitter;
    const form = e.target;
    if (!SpendTiers.enabled || e.defaultPrevented) return;
    if (!submitter || submitter.name !== 'checkout') return;
    if (form.dataset.spendTiersReady === '1') {
      delete form.dataset.spendTiersReady;
      return;
    }

    e.preventDefault();
    if (submitter.classList.contains('working')) return;
    submitter.classList.add('working');

    const setCodes = (codes) =>
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: codes.join(',') }),
      }).then((r) => {
        if (!r.ok) throw new Error('cart/update ' + r.status);
        return r.json();
      });

    let tierCode = null;
    fetchCart()
      .then((cart) => {
        const plan = codesFor(cart);
        tierCode = plan.tier && plan.tier.code;
        if (!plan.changed) return null;
        return setCodes(plan.codes).then((updated) => {
          // Safety net: tiers may be automatic discounts in Shopify. If Shopify doesn't
          // accept a tier code, take it back off so checkout shows no "not applicable" error.
          const rejected = (updated.discount_codes || [])
            .filter((d) => d.applicable === false && tierCodes.includes((d.code || '').toUpperCase()))
            .map((d) => d.code.toUpperCase());
          if (!rejected.length) return updated;
          tierCode = null;
          return setCodes(plan.codes.filter((c) => !rejected.includes(c)));
        });
      })
      .then(() => {
        submitter.classList.remove('working');
        form.dataset.spendTiersReady = '1';
        form.requestSubmit(submitter);
      })
      .catch((err) => {
        console.warn('[SpendTiers] could not apply tier code, using /discount/ redirect', err);
        // Save the form (note, ship date, quantities) first, then let Shopify apply the code.
        fetch('/cart/update.js', { method: 'POST', body: new FormData(form), credentials: 'same-origin' })
          .catch(() => {})
          .then(() => {
            window.location.href = tierCode
              ? '/discount/' + encodeURIComponent(tierCode) + '?redirect=/checkout'
              : '/checkout';
          });
      });
  });
})();
